// src/Layout.js
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import ScienceIcon from '@mui/icons-material/Science';
import { styled } from '@mui/system';
import { useAuth } from './AuthContext';
import { useAccess } from './useAccess';

const drawerWidth = 240;

const ModernListItemButton = styled(ListItemButton)({
  color: '#fff',
  borderRadius: 10,
  margin: '6px 10px',
  paddingTop: 10,
  paddingBottom: 10,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.10)',
    transform: 'translateX(2px)',
    transition: 'all 0.15s ease-in-out',
  },
});

const Layout = () => {
  const { logout, currentUser, isAdmin, loading: authLoading } = useAuth();
  const { access, canUsePaidFeatures, loading: accessLoading, openPaywall } = useAccess();

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // ✅ Makes Stripe click feel instant
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleStartTrial = async () => {
    if (checkoutLoading) return;
    try {
      setCheckoutLoading(true);
      await openPaywall(); // redirects on success
    } finally {
      // In practice, redirect will happen and this won't matter,
      // but keep it for safety if an error occurs.
      setCheckoutLoading(false);
    }
  };

  const getAccessChip = () => {
    if (accessLoading || authLoading) {
      return (
        <Chip
          size="small"
          label="Checking access…"
          sx={{ bgcolor: 'rgba(255,255,255,0.10)', color: '#fff' }}
        />
      );
    }

    const a = String(access || 'locked').toLowerCase();

    if (a === 'lifetime') {
      return (
        <Chip
          size="small"
          icon={<VerifiedIcon style={{ color: '#fff' }} />}
          label="Lifetime"
          sx={{ bgcolor: 'rgba(155, 89, 182, 0.30)', color: '#fff' }}
        />
      );
    }

    if (a === 'active') {
      return (
        <Chip
          size="small"
          icon={<VerifiedIcon style={{ color: '#fff' }} />}
          label="Active"
          sx={{ bgcolor: 'rgba(46, 204, 113, 0.25)', color: '#fff' }}
        />
      );
    }

    if (a === 'trialing') {
      return (
        <Chip
          size="small"
          icon={<ScienceIcon style={{ color: '#fff' }} />}
          label="Free trial"
          sx={{ bgcolor: 'rgba(52, 152, 219, 0.25)', color: '#fff' }}
        />
      );
    }

    if (a === 'beta') {
      return (
        <Chip
          size="small"
          icon={<ScienceIcon style={{ color: '#fff' }} />}
          label="Beta access"
          sx={{ bgcolor: 'rgba(241, 196, 15, 0.22)', color: '#fff' }}
        />
      );
    }

    if (a === 'past_due') {
      return (
        <Chip
          size="small"
          icon={<LockIcon style={{ color: '#fff' }} />}
          label="Payment issue"
          sx={{ bgcolor: 'rgba(231, 76, 60, 0.25)', color: '#fff' }}
        />
      );
    }

    if (a === 'canceled') {
      return (
        <Chip
          size="small"
          icon={<LockIcon style={{ color: '#fff' }} />}
          label="Canceled"
          sx={{ bgcolor: 'rgba(255,255,255,0.10)', color: '#fff' }}
        />
      );
    }

    return (
      <Chip
        size="small"
        icon={<LockIcon style={{ color: '#fff' }} />}
        label="Preview mode"
        sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' }}
      />
    );
  };

  const menuItems = [
    { text: 'Dashboard', path: '/' },
    { text: 'Participants', path: '/participants' },
    { text: 'Invoices', path: '/invoices' },
    { text: 'Provider Details', path: '/user-info' },
    { text: 'Service Agreements', path: '/agreements' },
    { text: 'Incident Reports', path: '/reports' },
    { text: 'Time Tracking', path: '/time-tracking' },
    { text: 'Export Totals', path: '/export' },
    { text: 'Account', path: '/account' },
  ];

  const drawerContent = (
    <Box
      sx={{
        width: drawerWidth,
        bgcolor: '#000',
        color: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              mb: 1,
              textAlign: 'center',
              color: '#fff',
              fontFamily: '"Questrial", sans-serif',
              fontWeight: 400,
              letterSpacing: '0.08em',
              fontSize: '1.4rem',
            }}
          >
            DashFlow
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
            {getAccessChip()}
          </Box>

          <List sx={{ px: 0 }}>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <ModernListItemButton
                  component={NavLink}
                  to={item.path}
                  onClick={isMobile ? handleDrawerToggle : undefined}
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? 'rgba(255,255,255,0.18)' : 'transparent',
                    fontWeight: isActive ? 700 : 400,
                  })}
                >
                  <ListItemText primary={item.text} primaryTypographyProps={{ sx: { color: '#fff' } }} />
                </ModernListItemButton>
              </ListItem>
            ))}

            {isAdmin && (
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ModernListItemButton
                  component={NavLink}
                  to="/admin"
                  onClick={isMobile ? handleDrawerToggle : undefined}
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? 'rgba(255,255,255,0.18)' : 'transparent',
                    fontWeight: isActive ? 700 : 400,
                  })}
                >
                  <ListItemText primary="Admin Panel" primaryTypographyProps={{ sx: { color: '#fff' } }} />
                </ModernListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Box>

      <Divider sx={{ bgcolor: '#222' }} />

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, color: '#fff' }}>
          {authLoading ? 'Checking login...' : `Logged in as: ${currentUser?.email || 'User'}`}
        </Typography>

        <Button
          variant="outlined"
          fullWidth
          onClick={handleLogout}
          sx={{
            borderColor: 'rgba(255,255,255,0.8)',
            color: '#fff',
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.10)',
              borderColor: '#fff',
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #111',
            backgroundColor: '#000',
            color: '#fff',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#000',
            color: '#fff',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            bgcolor: '#000',
            color: '#fff',
            pt: 'env(safe-area-inset-top)',
          }}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>

            <Typography
              noWrap
              sx={{
                color: '#fff',
                fontFamily: '"Questrial", sans-serif',
                fontWeight: 400,
                letterSpacing: '0.08em',
                fontSize: '1.1rem',
              }}
            >
              DashFlow
            </Typography>

            <Box sx={{ ml: 'auto' }}>{getAccessChip()}</Box>
          </Toolbar>
        </AppBar>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          p: { xs: 2, sm: 3, md: 4 },
          color: '#000',
          background: 'linear-gradient(180deg, #ffffff 0%, #f7f7f9 55%, #f6f6f6 100%)',
          minHeight: '100vh',
        }}
      >
        {isMobile && (
          <>
            <Toolbar />
            <Box sx={{ height: 'env(safe-area-inset-top)' }} />
          </>
        )}

        {!canUsePaidFeatures && !accessLoading && !authLoading && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.08)',
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, mb: 0.25 }}>Preview mode — actions are locked</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                You can browse the full app. Start a free trial to unlock saving, invoices, and PDF exports.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={handleStartTrial}
              disabled={checkoutLoading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}
            >
              {checkoutLoading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Opening checkout…
                </>
              ) : (
                'Start Free Trial'
              )}
            </Button>
          </Box>
        )}

        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;



